<template>
  <div v-if="ratings && ratings.length" class="event-ratings">
    <div
      v-for="(rating, index) in ratings"
      :key="rating.rating_scale_id != null ? rating.rating_scale_id : 'legacy-' + index"
      class="event-ratings__item"
    >
      <span class="event-ratings__name">{{ rating.name }}: </span>

      <!-- Named level: a pill tinted with the rating's color -->
      <span v-if="rating.display_type === 'labels'" class="event-ratings__level" :style="levelStyle(rating.color)">
        {{ rating.display }}
      </span>

      <!-- Icons: outline for empty, filled for full, left half filled for a half step -->
      <span v-else class="event-ratings__icons" :style="{ color: rating.color }" role="img" :aria-label="rating.display">
        <span
          v-for="n in iconCount(rating)"
          :key="n"
          class="event-ratings__icon"
          :class="'is-' + fill(rating, n)"
        >
          <i v-if="isCustomIcon(rating.icon)" class="event-ratings__outline tim-icons" :class="rating.icon"></i>
          <b-icon v-else class="event-ratings__outline" :icon="rating.icon"></b-icon>
          <span v-if="fill(rating, n) !== 'empty'" class="event-ratings__fill">
            <i v-if="isCustomIcon(rating.icon)" class="tim-icons" :class="rating.icon + '-fill'"></i>
            <b-icon v-else :icon="rating.icon + '-fill'"></b-icon>
          </span>
        </span>
      </span>
    </div>
  </div>
</template>

<script>
// Event ratings on the booking site (OTC-23): one line per rating the venue shows to customers,
// in the venue's scale order. `ratings` comes ready to render from the schedules feed
// (questgenie-gets-chedules-for-company-games): { name, display_type, max_value, icon, color,
// value, display }. Text color and font are inherited from the card around it.
// Console twin: otc-gm-console src/components/RatingDisplay.vue (same fill rules).
import { isCustomRatingIcon, ratingIconCount, ratingIconFill, ratingLevelStyle } from '@/util/eventRatings'

export default {
  name: 'EventRatings',
  props: {
    ratings: { type: Array, default: () => [] }
  },
  methods: {
    isCustomIcon: isCustomRatingIcon,
    iconCount: ratingIconCount,
    fill(rating, n) {
      return ratingIconFill(rating.value, n)
    },
    levelStyle: ratingLevelStyle
  }
}
</script>

<style scoped>
/* The wrapper adds no box of its own, so each rating lays out like the other card details
   (group size, duration) in whichever container the page uses. */
.event-ratings {
  display: contents;
}

.event-ratings__item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

/* A name or level longer than the column breaks instead of running past it. */
.event-ratings__name,
.event-ratings__level {
  max-width: 100%;
  overflow-wrap: anywhere;
}

/* Long scales (up to 10 icons) wrap inside a narrow column instead of running past it. */
.event-ratings__icons {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.2em;
  max-width: 100%;
  line-height: 1;
}

.event-ratings__icon {
  position: relative;
  display: inline-flex;
}

.event-ratings__icon.is-empty .event-ratings__outline,
.event-ratings__icon.is-half .event-ratings__outline {
  opacity: 0.4;
}

/* Filled icons carry their details as cut-outs (keyhole, skull eyes); the outline must not sit
   under the filled part or it fills those holes. */
.event-ratings__icon.is-full .event-ratings__outline {
  visibility: hidden;
}

.event-ratings__icon.is-half .event-ratings__outline {
  clip-path: inset(0 0 0 50%);
}

.event-ratings__fill {
  position: absolute;
  top: 0;
  left: 0;
  display: inline-flex;
}

.event-ratings__icon.is-half .event-ratings__fill {
  clip-path: inset(0 50% 0 0);
}

.event-ratings__level {
  display: inline-block;
  padding: 0 7px;
  border: 1px solid rgba(125, 125, 125, 0.35);
  border-radius: 9px;
  background: rgba(125, 125, 125, 0.12);
  font-size: 0.75em;
  font-weight: 600;
  line-height: 1.45;
}
</style>
